/**
 * SHER-210 — single source of truth for normalising whatever shape arrives in
 * `initialState.listElements` for SmartField. Both the useState initialiser
 * and the useUpdateEffect inside SmartField go through this helper so they
 * can't drift in their treatment of empty/null shapes (review feedback).
 *
 * Shapes seen in production:
 *   - already an array → returned as-is (covers the common case)
 *   - null / undefined / "" → empty array
 *   - JSON-stringified blob (legacy write paths for SINGLE_SMARTFIELDS) →
 *     parsed; if the parsed value is an array we return it, if null we return
 *     [], otherwise we wrap it; if parse fails we return []
 *   - any other primitive/object (e.g. saved BMI object
 *     {bmi, bmi_height, bmi_weight}) → wrapped in a single-element array
 *
 * Kept in its own file (not in SmartFields/index.tsx) so it's reachable from
 * Jest without pulling in the rest of the module's Vite-specific imports.
 */
import { bmiParts } from './bmiEntry';

export function normaliseInitialListElements<T = any>(raw: any): T[] {
    if (Array.isArray(raw)) return raw as T[];
    if (raw == null || raw === '') return [];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed as T[];
            if (parsed == null) return [];
            return [parsed as T];
        } catch (_) {
            return [];
        }
    }
    return [raw as T];
}

/**
 * SHER-210 (Pedro feedback 2026-05-04) — single parser for the BMI saved value
 * shape, used by three render sites:
 *   - SmartFields/index.tsx renderSingleSummary (form mode display)
 *   - show_records_section.jsx (patient read-only view)
 *   - SurveyRecordsTable.tsx renderValue (submissions list — was the React #31
 *     crash site Pedro hit on /patient/.../nurse)
 *
 * The shape on the wire varies: `{bmi, bmi_height, bmi_weight}`, sometimes
 * wrapped in a single-element array, occasionally a JSON-stringified blob.
 * Each render site needs a uniform `{bmi, height, weight}` view; centralising
 * here means the next "but it's slightly different here" report only needs one
 * fix.
 */
export interface BmiView {
    bmi: number | null;
    height: number | null;
    weight: number | null;
}

export function extractBmiValue(raw: any): BmiView {
    let candidate: any = raw;
    if (Array.isArray(candidate)) candidate = candidate[0];
    if (typeof candidate === 'string') {
        try { candidate = JSON.parse(candidate); } catch (_) { candidate = null; }
        if (Array.isArray(candidate)) candidate = candidate[0];
    }
    if (!candidate || typeof candidate !== 'object') {
        return { bmi: null, height: null, weight: null };
    }
    const numOrNull = (v: any): number | null => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
        return null;
    };
    return {
        bmi: numOrNull((candidate as any).bmi),
        height: numOrNull((candidate as any).bmi_height),
        weight: numOrNull((candidate as any).bmi_weight),
    };
}

/**
 * Plain-string formatter used by SurveyRecordsTable.tsx's flat row cells where
 * a stacked layout doesn't fit. SHER-1045: weight and height are recorded
 * independently, so without an IMC the cell shows whichever measurement exists;
 * `'—'` only when nothing was recorded.
 */
export function formatBmiInline(view: BmiView): string {
    const parts = bmiParts(view);
    const measurements = [parts.height, parts.weight].filter((part): part is string => part != null);
    if (parts.bmi == null) return measurements.length > 0 ? measurements.join(' / ') : '—';
    // Legacy rows can carry an IMC with one measurement missing: show the IMC alone.
    if (measurements.length < 2) return parts.bmi;
    return `${parts.bmi} (${measurements.join(' / ')})`;
}
